/**
 * Universal Storage Adapter for FlowHR
 *
 * This hook provides a unified interface for data persistence that works across:
 * - Web (using localStorage) - Primary mode for GitHub Spark
 * - iOS/Android (using Capacitor Preferences API) - When available
 *
 * It mimics the GitHub Spark useKV hook API for seamless migration.
 */

import { useCallback, useEffect, useState } from "react";

// Detect if we're in a Capacitor environment
const isCapacitorAvailable = () => {
  try {
    return typeof window !== 'undefined' && 'Capacitor' in window;
  } catch {
    return false;
  }
};

// Check if we're on a native platform
const isNativePlatform = isCapacitorAvailable() &&
  (window as any).Capacitor?.isNativePlatform?.() === true;

/**
 * Universal key-value storage hook
 *
 * @template T - Type of the stored value
 * @param {string} key - Storage key
 * @param {T} defaultValue - Default value if key doesn't exist
 * @returns {[T, (value: T) => void]} - Tuple of [value, setValue]
 *
 * @example
 * const [user, setUser] = useKV<User>('user-profile', { name: 'John' })
 */
export function useKV<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prevValue: T) => T)) => void] {
  const [value, setValue] = useState<T>(defaultValue);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load initial value from storage
  useEffect(() => {
    const loadValue = async () => {
      try {
        if (isNativePlatform && isCapacitorAvailable()) {
          // Mobile: Use Capacitor Preferences API
          const Preferences = (window as any).Capacitor?.Plugins?.Preferences;
          if (Preferences) {
            const { value: stored } = await Preferences.get({ key });
            if (stored !== null) {
              const parsed = JSON.parse(stored);
              setValue(parsed);
            }
          }
        } else {
          // Web: Use localStorage (GitHub Spark fallback)
          const stored = localStorage.getItem(key);
          if (stored !== null) {
            const parsed = JSON.parse(stored);
            setValue(parsed);
          }
        }
      } catch (error) {
        console.error(`Error loading value for key "${key}":`, error);
        // Keep default value on error
      } finally {
        setIsInitialized(true);
      }
    };

    loadValue();
  }, [key]);

  // Update value in storage
  const updateValue = useCallback(
    async (newValueOrUpdater: T | ((prevValue: T) => T)) => {
      // Handle both direct values and updater functions
      const newValue =
        typeof newValueOrUpdater === "function"
          ? (newValueOrUpdater as (prevValue: T) => T)(value)
          : newValueOrUpdater;

      setValue(newValue);

      try {
        const serialized = JSON.stringify(newValue);

        if (isNativePlatform && isCapacitorAvailable()) {
          // Mobile: Use Capacitor Preferences API
          const Preferences = (window as any).Capacitor?.Plugins?.Preferences;
          if (Preferences) {
            await Preferences.set({ key, value: serialized });
          }
        } else {
          // Web: Use localStorage
          localStorage.setItem(key, serialized);
        }
      } catch (error) {
        console.error(`Error saving value for key "${key}":`, error);
      }
    },
    [key, value]
  );

  return [value, updateValue];
}

/**
 * Hook to check if running on native mobile platform
 *
 * @returns {boolean} True if running on iOS or Android
 *
 * @example
 * const isNative = useIsNativePlatform()
 * if (isNative) {
 *   // Show mobile-specific UI
 * }
 */
export function useIsNativePlatform(): boolean {
  return isNativePlatform;
}

/**
 * Hook to get current platform information
 *
 * @returns {object} Platform details
 *
 * @example
 * const platform = usePlatform()
 * console.log(platform.name) // 'ios' | 'android' | 'web'
 */
export function usePlatform() {
  const capacitor = isCapacitorAvailable() ? (window as any).Capacitor : null;
  const platformName = capacitor?.getPlatform?.() || 'web';

  return {
    name: platformName,
    isNative: isNativePlatform,
    isIOS: platformName === "ios",
    isAndroid: platformName === "android",
    isWeb: platformName === "web",
  };
}

/**
 * Utility function to clear all stored data
 * USE WITH CAUTION: This will delete all app data
 *
 * @example
 * await clearAllStorage()
 */
export async function clearAllStorage(): Promise<void> {
  try {
    if (isNativePlatform && isCapacitorAvailable()) {
      const Preferences = (window as any).Capacitor?.Plugins?.Preferences;
      if (Preferences) {
        await Preferences.clear();
      }
    } else {
      localStorage.clear();
    }
  } catch (error) {
    console.error("Error clearing storage:", error);
    throw error;
  }
}

/**
 * Utility function to get all storage keys
 * Useful for debugging and data migration
 *
 * @returns {Promise<string[]>} Array of all storage keys
 *
 * @example
 * const keys = await getAllKeys()
 * console.log('Stored keys:', keys)
 */
export async function getAllKeys(): Promise<string[]> {
  try {
    if (isNativePlatform && isCapacitorAvailable()) {
      const Preferences = (window as any).Capacitor?.Plugins?.Preferences;
      if (Preferences) {
        const { keys } = await Preferences.keys();
        return keys;
      }
      return [];
    } else {
      return Object.keys(localStorage);
    }
  } catch (error) {
    console.error("Error getting storage keys:", error);
    return [];
  }
}

/**
 * Utility function to remove a specific key from storage
 *
 * @param {string} key - Key to remove
 *
 * @example
 * await removeKey('old-data')
 */
export async function removeKey(key: string): Promise<void> {
  try {
    if (isNativePlatform && isCapacitorAvailable()) {
      const Preferences = (window as any).Capacitor?.Plugins?.Preferences;
      if (Preferences) {
        await Preferences.remove({ key });
      }
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error removing key "${key}":`, error);
    throw error;
  }
}
